import React, { useState, useEffect, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { isEmpty } from 'lodash'
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { Eye as EyeIcon } from 'react-feather'

import AgeGatePreviewModal from './AgeGatePreviewModal'
import LanguageCustomization from '../BannerCustomizationCard/components/LanguageCustomization'

import useCookieBanner from 'hooks/useCookieBanner'
import useSnackbar from 'hooks/useSnackbar'

import { ageGateSchema } from '../../stepSchemas'
import * as service from 'service'
import constants from 'constants/index'
import theme from 'theme'

const {
  AGE_GATE_CARD_TABS,
  AGE_GATE_OVERLAY_OPACITY_MARKS,
  AGE_VERIFICATION_TAB_ID,
  AGE_BLOCKING_TAB_ID,
} = constants.cookies.banner

const OPACITY_OPTIONS = AGE_GATE_OVERLAY_OPACITY_MARKS.map((m) => m.value)

const {
  BLOCKING_ACTION_REDIRECT,
  BLOCKING_ACTION_OPTIONS,
  TEXT_FIELDS,
  ORDERED_TEXT_FIELDS,
  ORDERED_TEXT_FIELDS_BLOCKING,
  TEXT_FIELD_CONFIG,
  TEXT_FIELD_CONFIG_BLOCKING,
  TEXT_FIELD_PLACEHOLDERS,
  TEXT_FIELD_BLOCKING_PLACEHOLDERS,
} = constants.cookies.ageGateTextOverride

const AgeGateCard = () => {
  const { register, errors, control, handleSubmit, reset, watch } = useForm({
    validationSchema: ageGateSchema,
    defaultValues: {
      ageGateEnabled: false,
      ageGateMinimumAge: 18,
      ageGateOverlayOpacity: 50,
      ageGateBlockingAction: BLOCKING_ACTION_OPTIONS[0].value,
      ageGateRedirectUrl: '',
    },
  })
  const snackbar = useSnackbar()

  const {
    ageGate,
    appearanceValues,
    banner,
    ageGateTextsOverrides,
    setAgeGateTextsOverrides,
    updateAgeGateTextOverride,
    getAgeGateTextOverride,
    selectedLanguage,
    nextStep,
    setIsAgeGateCompiling,
    setIsSubmitting,
    setStepSubmit,
    refreshAgeGate,
  } = useCookieBanner()

  const [selectedTab, setSelectedTab] = useState(AGE_VERIFICATION_TAB_ID)
  const [previewOpen, setPreviewOpen] = useState(false)

  const handleOpenPreview = useCallback(() => setPreviewOpen(true), [])
  const handleClosePreview = useCallback(() => setPreviewOpen(false), [])

  const ageGateExists = !!ageGate

  const ageGateOverlayOpacity = watch('ageGateOverlayOpacity')
  const ageGateBlockingAction = watch('ageGateBlockingAction')

  const smDown = useMediaQuery(theme.breakpoints.down('sm'), {
    defaultMatches: true,
  })
  const mdUp = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })

  useEffect(() => {
    if (!ageGate) return

    reset({
      ageGateEnabled: ageGate.ageGateEnabled ?? false,
      ageGateMinimumAge: ageGate.ageGateMinimumAge ?? 18,
      ageGateOverlayOpacity: ageGate.ageGateOverlayOpacity ?? 50,
      ageGateBlockingAction:
        ageGate.ageGateBlockingAction ?? BLOCKING_ACTION_OPTIONS[0].value,
      ageGateRedirectUrl: ageGate.ageGateRedirectUrl ?? '',
    })

    if (ageGate.ageGateTextsOverrides) {
      setAgeGateTextsOverrides(ageGate.ageGateTextsOverrides)
    }
  }, [ageGate])

  const handleTabChange = (_, clickedTab) => setSelectedTab(clickedTab)

  const handleChange = (fieldKey) => (e) => {
    updateAgeGateTextOverride(fieldKey, e.target.value)
  }

  const getPlaceholder = (fieldKey, placeholders) =>
    placeholders?.[selectedLanguage]?.[fieldKey] ?? ''

  const handleFormSubmit = useCallback(
    async (data) => {
      try {
        setIsSubmitting(true)

        const ageGateData = {
          ageGateEnabled: data.ageGateEnabled,
          ageGateMinimumAge: data.ageGateMinimumAge || null,
          ageGateOverlayOpacity: data.ageGateOverlayOpacity,
          ageGateBlockingAction: data.ageGateBlockingAction,
          ageGateRedirectUrl: data.ageGateRedirectUrl || null,
          ageGateTextTemplateId: data.textTemplateId || null,
        }

        const ageGateService = ageGateExists
          ? service.cookies.ageGate.update
          : service.cookies.ageGate.create

        await ageGateService({ bannerId: banner?.id, ageGate: ageGateData })

        if (!isEmpty(ageGateTextsOverrides)) {
          await service.cookies.ageGateTextOverride.manage({
            bannerId: banner?.id,
            ageGateTextsOverrides,
          })
        }

        snackbar.open({
          variant: 'success',
          message:
            constants.cookies.banner.SUCCESS_MESSAGES[
              constants.cookies.banner.STEPS.AGE_GATE
            ],
        })

        try {
          await service.cookies.ageGate.compile({ bannerId: banner?.id })
          setIsAgeGateCompiling(true)
          refreshAgeGate()
        } catch {
          // compile pode falhar se o age gate não estiver habilitado, não bloqueia o fluxo
        }

        nextStep()
      } catch (error) {
        snackbar.open({
          variant: 'error',
          message: 'Erro ao salvar controle de idade. Tente novamente.',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      ageGateExists,
      ageGateTextsOverrides,
      banner,
      setIsSubmitting,
      snackbar,
      setIsAgeGateCompiling,
      refreshAgeGate,
      nextStep,
    ],
  )

  useEffect(() => {
    setStepSubmit(handleSubmit(handleFormSubmit))
  }, [handleSubmit, handleFormSubmit, setStepSubmit])

  return (
    <Paper
      variant="outlined"
      id={constants.cookies.banner.COOKIES_BANNER_SETTING_DRIVER_STEP_9}
    >
      <Box p={2} display="flex" alignItems="center" gridGap={theme.spacing(1)}>
        <Controller
          name="ageGateEnabled"
          control={control}
          as={<Switch color="primary" size="small" />}
          onChange={([event]) => event.target.checked}
        />
        <Typography color="primary" variant="h5">
          Controle de Idade
        </Typography>
      </Box>
      <Divider />
      <Box
        p={2}
        display="flex"
        flexDirection="column"
        gridGap={theme.spacing(2)}
      >
        <Tabs
          value={selectedTab}
          variant="scrollable"
          onChange={handleTabChange}
        >
          {AGE_GATE_CARD_TABS.map((tab) => (
            <Tab key={tab.id} label={tab.tabName} value={tab.id} />
          ))}
        </Tabs>

        <Grid container spacing={smDown ? 2 : 0}>
          <Grid item xs={12} md={7}>
            <Box display="flex" flexDirection="row" height="100%">
              <Box
                display="flex"
                flexDirection="column"
                width="100%"
                gridGap={theme.spacing(2)}
                pr={mdUp ? 2 : 0}
              >
                <Grid
                  container
                  spacing={2}
                  style={{
                    display:
                      selectedTab === AGE_VERIFICATION_TAB_ID ? '' : 'none',
                  }}
                >
                  <Grid item xs={12}>
                    <LanguageCustomization />
                  </Grid>
                  <Grid item xs={8} sm={9}>
                    <TextField
                      fullWidth
                      label={TEXT_FIELD_CONFIG[TEXT_FIELDS.TITLE].label}
                      value={getAgeGateTextOverride(TEXT_FIELDS.TITLE)}
                      onChange={handleChange(TEXT_FIELDS.TITLE)}
                      InputLabelProps={{ shrink: true }}
                      placeholder={getPlaceholder(
                        TEXT_FIELDS.TITLE,
                        TEXT_FIELD_PLACEHOLDERS,
                      )}
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <TextField
                      name="ageGateMinimumAge"
                      inputRef={register()}
                      error={!!errors?.ageGateMinimumAge}
                      helperText={errors?.ageGateMinimumAge?.message}
                      label="Idade mínima:"
                      type="number"
                      color="primary"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  {ORDERED_TEXT_FIELDS.filter(
                    (fieldKey) => fieldKey !== TEXT_FIELDS.TITLE,
                  ).map((fieldKey) => {
                    const config = TEXT_FIELD_CONFIG[fieldKey]
                    if (!config) return null
                    const isButtonField =
                      fieldKey.startsWith('ageGate.accept') ||
                      fieldKey.startsWith('ageGate.reject')
                    return (
                      <Grid item xs={isButtonField ? 6 : 12} key={fieldKey}>
                        <TextField
                          fullWidth
                          label={config.label}
                          value={getAgeGateTextOverride(fieldKey)}
                          onChange={handleChange(fieldKey)}
                          multiline={config.multiline}
                          minRows={config.multiline ? 3 : 1}
                          InputLabelProps={{ shrink: true }}
                          placeholder={getPlaceholder(
                            fieldKey,
                            TEXT_FIELD_PLACEHOLDERS,
                          )}
                        />
                      </Grid>
                    )
                  })}
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Opacidade da tela:
                    </Typography>
                    <Controller
                      name="ageGateOverlayOpacity"
                      control={control}
                      as={
                        <ToggleButtonGroup exclusive>
                          {OPACITY_OPTIONS.map((option) => (
                            <ToggleButton key={option} value={option}>
                              {option}%
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      }
                      onChange={([, newValue]) =>
                        newValue !== null ? newValue : ageGateOverlayOpacity
                      }
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  spacing={2}
                  style={{
                    display: selectedTab === AGE_BLOCKING_TAB_ID ? '' : 'none',
                  }}
                >
                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel color="primary">
                        <Typography variant="subtitle2" color="textPrimary">
                          <strong>
                            O que quer fazer caso o usuário seja menor?
                          </strong>
                        </Typography>
                      </FormLabel>
                      <Controller
                        name="ageGateBlockingAction"
                        control={control}
                        as={
                          <RadioGroup>
                            {BLOCKING_ACTION_OPTIONS.map((option) => (
                              <FormControlLabel
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                control={<Radio color="primary" />}
                              />
                            ))}
                          </RadioGroup>
                        }
                        mode="onChange"
                      />
                    </FormControl>
                  </Grid>
                  {ORDERED_TEXT_FIELDS_BLOCKING.map((fieldKey) => {
                    const config = TEXT_FIELD_CONFIG_BLOCKING[fieldKey]
                    if (!config) return null
                    return (
                      <Grid item xs={12} key={fieldKey}>
                        <TextField
                          fullWidth
                          label={config.label}
                          value={getAgeGateTextOverride(fieldKey)}
                          onChange={handleChange(fieldKey)}
                          multiline={config.multiline}
                          minRows={config.multiline ? 3 : 1}
                          InputLabelProps={{ shrink: true }}
                          placeholder={getPlaceholder(
                            fieldKey,
                            TEXT_FIELD_BLOCKING_PLACEHOLDERS,
                          )}
                        />
                      </Grid>
                    )
                  })}
                  <Grid
                    item
                    xs={12}
                    style={{
                      display:
                        ageGateBlockingAction === BLOCKING_ACTION_REDIRECT
                          ? ''
                          : 'none',
                    }}
                  >
                    <TextField
                      name="ageGateRedirectUrl"
                      inputRef={register()}
                      error={!!errors?.ageGateRedirectUrl}
                      helperText={errors?.ageGateRedirectUrl?.message}
                      label="Redirecionar para URL:"
                      type="url"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Box>
              {mdUp && <Divider orientation="vertical" variant="middle" />}
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              minHeight={theme.spacing(32)}
              pl={mdUp ? 3 : 0}
              gridGap={theme.spacing(3)}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                height="100%"
                border={`1px dashed ${theme.palette.divider}`}
                borderRadius={4}
                p={3}
                gridGap={theme.spacing(2)}
              >
                <EyeIcon
                  size={48}
                  color={theme.palette.text.disabled}
                  strokeWidth={1}
                />
                <Typography
                  variant="body2"
                  align="center"
                  color="textSecondary"
                >
                  Clique em Visualizar para ver como o modal de verificação de
                  idade será exibido
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenPreview}
                >
                  Visualizar
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <AgeGatePreviewModal
          open={previewOpen}
          onClose={handleClosePreview}
          overlayOpacity={ageGateOverlayOpacity}
          selectedLanguage={selectedLanguage}
          getAgeGateTextOverride={getAgeGateTextOverride}
          primaryColor={appearanceValues?.primaryColor}
          secondaryColor={appearanceValues?.secondaryColor}
          fontFamily={appearanceValues?.fontFamily}
        />
        {(selectedTab === AGE_BLOCKING_TAB_ID ||
          selectedTab === AGE_VERIFICATION_TAB_ID) && (
          <Alert severity="warning">
            A autodeclaração de idade não é suficiente para sites com conteúdo
            restrito. Se o seu site oferece{' '}
            <strong>produtos ou conteúdos impróprios</strong> ou proibidos para
            menores de 18 anos, (como{' '}
            <strong>apostas, bebidas alcoólicas ou pornografia</strong>), o
            banner não pode utilizar apenas autodeclaração. Nesses casos será
            necessário utilizar mecanismos confiáveis de verificação, como
            criação de conta ou verificação documental.
          </Alert>
        )}
      </Box>
    </Paper>
  )
}

export default AgeGateCard
